import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import ConfirmationButtons from "@/common/ConfirmationButtons";
import { motion } from "framer-motion";
import { Container } from "@/common/ui/container";
import OTPInputBox from "@/common/ui/otp/OTPInputBox";
import { toastErrorMessage, toastMessage, toastSuccessMessage } from "@/utils/helper/myToast";
import { Check, Copy } from "lucide-react";
import { H3, LightP, LightText } from "@/common/ui/Text";
import { Image } from "@/common/ui/Image";
import { verifyMfa } from "@/services/common/security.services";
import { Col, Row } from "@/common/css";

interface AuthenticatorSetupProps {
  onClose: (props?: any) => void;
  mfaDetails: { secret: string; otpauth_url: string };
}

const AuthenticatorSetup: React.FC<AuthenticatorSetupProps> = ({ onClose, mfaDetails }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleVerifyAuthenticator = async () => {
    setIsLoading(true);
    try {
      const res: any = await verifyMfa({ totp: otp, mfaType: "AUTHENTICATOR" });

      if (res?.status === 200) {
        const { message, isError } = res.data;
        onClose();
        toastMessage(message, isError);
      } else if (res?.response?.status === 401) {
        const { message } = res?.response?.data || {};
        setError(message);
        return;
      } else {
        const { message } = res?.response?.data || {};
        if (message) {
          setError(message);
        }
      }
    } catch (error) {
      console.error("Verification error", error);
      toastMessage("An error occurred during verification.", true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    if (navigator?.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setIsCopied(true);
          toastSuccessMessage("Secrete Key copied to clipboard!");
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch((err) => {
          toastErrorMessage("Failed to copy token.");
          console.error("Error copying to clipboard: ", err);
        });
    }
  };

  return (
    <Container.ContentBox>
      <Container.Content className="flex !px-3 md:!px-4 flex-col gap-3 md:gap-5 max-h-[calc(100vh-120px)] overflow-y-auto">
        <Col className="gap-y-2">
          <H3 text="STEP 1" className="text-text-default" />
          <Row className=" gap-2">
            <Image src="/images/google-authenticator-qr-link.svg" alt="otp-send-image" className="size-32" />
            <Col className="">
              <H3 text="Scan and download Google authentication app" className="text-text-default" />
              <LightText text="Download and install Google authenticator app for your phone or tablet" className=" pt-1 py-4" />
            </Col>
          </Row>
        </Col>
        <Col className="gap-y-2">
          <H3 text="STEP 2" className="text-text-default" />
          <Row className="gap-2">
            <QRCodeSVG value={mfaDetails.otpauth_url} className="min-w-[128px]" />
            <Col className=" md:w-[calc(100%-128px-40px)]">
              <H3 text="Scan the QR code" className="text-text-default" />
              <LightP text="Open the authentication app and scan the image to the left using your phone’s camera." className=" pt-1 py-4" />
              <LightText text="2FA key (Manual entry)" className="!font-bold" />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 relative">
                <input
                  type="text"
                  value={mfaDetails.secret}
                  readOnly
                  className="w-full p-2 pr-10 text-13 font-normal border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute right-2 top-3"
                  onClick={() => handleCopy(mfaDetails.secret)}
                >
                  {isCopied ? <Check className="text-green-500" size={20} /> : <Copy className="text-gray-500" size={20} />}
                </motion.button>
              </motion.div>
            </Col>
          </Row>
        </Col>
        <Col className="gap-y-2">
          <H3 text="STEP 3" className="text-text-default" />
          <Row className="gap-2 flex-col md:flex-row">
            <Image src="/images/otp-send-transparent.svg" alt="otp-send-image" className="size-32" />
            <Col className="">
              <H3 text="Activate with Code" className="text-text-default" />
              <LightText text="Enter the 6-digit verification code generated" className=" pt-1 py-4" />
              <OTPInputBox resetCount={0} setCompleteOtp={setOtp} errorMessage={error} resetErrorMessage={() => setError("")} />
            </Col>
          </Row>
        </Col>
      </Container.Content>
      <Container.Separator />
      <ConfirmationButtons handleConfirm={handleVerifyAuthenticator} handleCancel={onClose} confirmText="Activate" disabled={!otp} isLoading={isLoading} />
    </Container.ContentBox>
  );
};

export default AuthenticatorSetup;
